import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatsCard from '../admin-dashboard/components/StatsCard';
import RevenueChart from './components/RevenueChart';
import TransactionsList from './components/TransactionsList';
import AddTransactionModal from './components/AddTransactionModal';

const RevenueDashboard = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    transactionCount: 0,
    averageTransaction: 0
  });
  const [transactions, setTransactions] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [dateRange, setDateRange] = useState('30');

  const categories = [
    { id: 'all', label: 'All Categories', icon: 'DollarSign' },
    { id: 'membership', label: 'Memberships', icon: 'Users' },
    { id: 'class', label: 'Classes', icon: 'Calendar' },
    { id: 'personal_training', label: 'Personal Training', icon: 'Dumbbell' },
    { id: 'merchandise', label: 'Merchandise', icon: 'ShoppingBag' },
    { id: 'other', label: 'Other', icon: 'MoreHorizontal' }
  ];

  useEffect(() => {
    if (!authLoading && (!user || !isGymOwner)) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && isGymOwner) {
      loadRevenueData();
    }
  }, [currentTenant, isGymOwner, filterCategory, dateRange]);

  const loadRevenueData = async () => {
    setLoading(true);
    try {
      const daysAgo = parseInt(dateRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      let query = supabase
        ?.from('revenue_transactions')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('transaction_date', startDate?.toISOString()?.split('T')?.[0])
        ?.order('transaction_date', { ascending: false });

      if (filterCategory !== 'all') {
        query = query?.eq('category', filterCategory);
      }

      const { data, error } = await query;

      if (error) throw error;

      setTransactions(data || []);

      // Calculate stats
      const totalRevenue = data?.reduce((sum, t) => sum + parseFloat(t?.amount), 0) || 0;
      const transactionCount = data?.length || 0;
      const averageTransaction = transactionCount > 0 ? totalRevenue / transactionCount : 0;

      // Calculate monthly revenue (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
      const monthlyRevenue = data
        ?.filter(t => new Date(t?.transaction_date) >= thirtyDaysAgo)
        ?.reduce((sum, t) => sum + parseFloat(t?.amount), 0) || 0;

      setStats({
        totalRevenue,
        monthlyRevenue,
        transactionCount,
        averageTransaction
      });

      // Prepare chart data (group by date)
      const chartMap = {};
      data?.forEach(t => {
        const date = t?.transaction_date;
        if (!chartMap?.[date]) {
          chartMap[date] = 0;
        }
        chartMap[date] += parseFloat(t?.amount);
      });

      const chartArray = Object?.entries(chartMap)?.map(([date, amount]) => ({
        date,
        amount
      }))?.sort((a, b) => new Date(a?.date) - new Date(b?.date));

      setChartData(chartArray);
    } catch (error) {
      console.error('Error loading revenue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = async (transactionData) => {
    try {
      const { error } = await supabase
        ?.from('revenue_transactions')
        ?.insert([{
          tenant_id: currentTenant?.id,
          ...transactionData
        }]);

      if (error) throw error;

      setShowAddModal(false);
      loadRevenueData();
    } catch (error) {
      console.error('Error adding transaction:', error);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!confirm('Are you sure you want to delete this transaction?')) return;

    try {
      const { error } = await supabase
        ?.from('revenue_transactions')
        ?.delete()
        ?.eq('id', transactionId);

      if (error) throw error;

      loadRevenueData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading revenue data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="mx-auto max-w-[1440px] px-4 md:px-6 py-6 md:py-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Revenue Dashboard</h1>
              <p className="text-muted-foreground">Track your gym's financial performance</p>
            </div>
            <Button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2"
            >
              <Icon name="Plus" size={20} />
              <span className="hidden md:inline">Add Transaction</span>
            </Button>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide flex-1">
              {categories?.map((cat) => (
                <button
                  key={cat?.id}
                  onClick={() => setFilterCategory(cat?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-smooth ${
                    filterCategory === cat?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-muted-foreground hover:bg-accent'
                  }`}
                >
                  <Icon name={cat?.icon} size={18} />
                  <span className="text-sm font-medium">{cat?.label}</span>
                </button>
              ))}
            </div>

            {/* Date Range Filter */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="px-4 py-2 rounded-lg bg-card border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          <StatsCard
            icon="DollarSign"
            iconColor="var(--color-success)"
            title="Total Revenue"
            value={`$${stats?.totalRevenue?.toFixed(2)}`}
            subtitle={`Last ${dateRange} days`}
            trend="up"
            trendValue="+12%"
          />
          <StatsCard
            icon="TrendingUp"
            iconColor="var(--color-primary)"
            title="Monthly Revenue"
            value={`$${stats?.monthlyRevenue?.toFixed(2)}`}
            subtitle="Last 30 days"
            trend="up"
            trendValue="+8%"
          />
          <StatsCard
            icon="Receipt"
            iconColor="var(--color-accent)"
            title="Transactions"
            value={stats?.transactionCount?.toString()}
            subtitle={`Last ${dateRange} days`}
            trend="up"
            trendValue="+15"
          />
          <StatsCard
            icon="Calculator"
            iconColor="var(--color-info)"
            title="Avg Transaction"
            value={`$${stats?.averageTransaction?.toFixed(2)}`}
            subtitle="Per transaction"
            trend="neutral"
            trendValue="--"
          />
        </div>

        {/* Revenue Chart */}
        <div className="mb-6 md:mb-8">
          <RevenueChart data={chartData} />
        </div>

        {/* Transactions List */}
        <TransactionsList
          transactions={transactions}
          onDelete={handleDeleteTransaction}
        />
      </main>

      {/* Add Transaction Modal */}
      {showAddModal && (
        <AddTransactionModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddTransaction}
        />
      )}

      <MobileBottomNav />
    </div>
  );
};

export default RevenueDashboard;